import { ApiService } from '@/api';
import {
  Geek,
  GeekItem,
  GeekItemWithJobTitle,
  GeekNames,
  GeekWithStatus,
  GeekJobInfo,
  SearchFilter, PhraseSearchFilter,
} from '@/models';

export const GEEKS_WITH_STATUSES_LIMIT = 25;

export class GeeksApi {
  private readonly baseUrl = '/api/geeks';

  public async fetchCurrent(): Promise<Geek> {
    const response = await ApiService.get(`${this.baseUrl}/self`);

    return response.json();
  }

  public async setNick(nick: string): Promise<Geek> {
    const response = await ApiService.put(
      `${this.baseUrl}/self/nick`,
      nick,
    );

    return response.json();
  }

  public async setNames(geekNames: GeekNames): Promise<void> {
    await ApiService.put(`${this.baseUrl}/self/names`, geekNames);
  }

  public async setAvatar(imageType: string, data: string): Promise<void> {
    await ApiService.put(
      `${this.baseUrl}/self/avatar`,
      { imageType, data },
    );
  }

  public async fetchGeekBasicInfo(id: string = 'self'): Promise<GeekItem> {
    const response = await ApiService.get(`${this.baseUrl}/${id}/basic-info`);

    return response.json();
  }

  public async fetchGeeks(searchPhrase: string): Promise<GeekItem[]> {
    const response = await ApiService.get(`${this.baseUrl}`, { params: { searchPhrase } });

    return response.json();
  }

  public async fetchAdmins(): Promise<GeekItemWithJobTitle[]> {
    const response = await ApiService.get(`${this.baseUrl}/admins`);

    return response.json();
  }

  public async fetchSuperAdmins(): Promise<GeekItemWithJobTitle[]> {
    const response = await ApiService.get(`${this.baseUrl}/super-admins`);

    return response.json();
  }

  public async fetchCsrfToken(): Promise<string> {
    const response = await ApiService.get(`${this.baseUrl}/csrf-token`);

    return response.text();
  }

  public async fetchGeeksWithStatuses(
    filters: PhraseSearchFilter,
    offset: number = 0,
    limit: number = GEEKS_WITH_STATUSES_LIMIT,
  ): Promise<{ data: GeekWithStatus[], totalCount: number }> {
    const response = await ApiService.get(
      `${this.baseUrl}/with-statuses`,
      {params: {offset, limit, searchPhrase: filters?.searchPhrase}},
    );
    const data: GeekWithStatus[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({ data, totalCount }));
  }

  public async activateGeek(id: string): Promise<void> {
    await ApiService.put(`${this.baseUrl}/${id}/activated`);
  }

  public async deactivateGeek(id: string): Promise<void> {
    await ApiService.put(`${this.baseUrl}/${id}/deactivated`);
  }

  public async fetchGeekJobInfo(): Promise<GeekJobInfo> {
    const response = await ApiService.get(`${this.baseUrl}/self/job-info`);

    return response.json();
  }
}
